import React from 'react';
import { components } from 'react-select';
import { PlayIcon } from '@rsos/assets/icons';
import { Text, Box } from '@rsos/components/capstone/base';
import { soundFileMapper } from '@rsos/constants';
import { captureExceptionWithScope } from '@rsos/utils/sentry';
import useIsIRP from '@rsos/utils/useIsIRP';
import { trackAudioTonePreviewed } from '../audioSettingsTrackings';

const SelectedRow = props => {
  const { data: sound } = props;

  return (
    <Box horizontal align="center" justify="space-between" pr={2}>
      <Text size="normal" color="primaryText">
        {sound.label}
      </Text>
    </Box>
  );
};
const GenericRow = props => {
  const { irpVersion } = useIsIRP();
  const { data: sound } = props;
  const audioElement = new Audio(sound.data.path);

  const handleAudioPlay = e => {
    e.nativeEvent.stopImmediatePropagation();
    e.stopPropagation();
    audioElement.play().catch(error => {
      // If the DOMException name is NotAllowedError, do not send it to Sentry,
      // otherwise, report the DOMException error.
      // Swallow NotAllowedError: The play method is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.
      if (error.name !== 'NotAllowedError') {
        captureExceptionWithScope(error, {
          extra: {
            audioName: sound.data.name,
            audioSrc: sound.data.path,
            errorMessage: error?.message,
          },
        });
      }
    });
    trackAudioTonePreviewed(irpVersion, sound.data.name);
  };
  return (
    <>
      <Box horizontal align="center" justify="space-between">
        <Text size="normal" color="primaryText">
          {sound.label}
        </Text>
        <Box
          horizontal
          color="secondaryText"
          align="center"
          flow={10}
          onClick={e => handleAudioPlay(e)}
          cursor="pointer"
        >
          <PlayIcon width={14} height={14} color="secondaryLink" />
        </Box>
      </Box>
    </>
  );
};
const ValueComponent = props => (
  <components.SingleValue {...props}>
    <SelectedRow {...props} />
  </components.SingleValue>
);
const OptionComponent = props => (
  <components.Option {...props}>
    <GenericRow {...props} />
  </components.Option>
);
export const customComponents = {
  Option: OptionComponent,
  SingleValue: ValueComponent,
};
export const customValueStyle = {
  control: (styles, state) => ({
    ...styles,
    height: '45px',
    opacity: `${state.isDisabled ? 0.4 : 1}`,
    background: `${state.selectProps.theme.colors.foreground}`,
    borderColor: `${
      state.menuIsOpen || state.isFocused
        ? state.selectProps.theme.colors.primaryBase
        : state.selectProps.theme.colors.heavyLineOutline
    } !important`,
    borderRadius:
      state.selectProps.noGap && state.selectProps.menuIsOpen
        ? '5px 5px 0 0'
        : 5,
    boxShadow: 'none',
    color: state.selectProps.theme.colors.primaryText,
  }),
  menuList: styles => ({
    ...styles,
    maxHeight: '120px',
  }),
};
export const buildOption = sound => ({
  label: sound.displayName,
  value: sound.name,
  data: sound,
});

export const getFormattedSoundValue = (type, tone) => {
  const alertsDefaultSound = soundFileMapper.find(s => s.name === 'glassVibes');
  const callsDefaultSound = soundFileMapper.find(s => s.name === 'xyloToggle');
  const smsDefaultSound = soundFileMapper.find(s => s.name === 'tripleVibe');

  const defaultSoundByType = {
    calls: callsDefaultSound && buildOption(callsDefaultSound),
    alerts: alertsDefaultSound && buildOption(alertsDefaultSound),
    sms: smsDefaultSound && buildOption(smsDefaultSound),
    default: alertsDefaultSound && buildOption(alertsDefaultSound),
  };

  if (tone) {
    const existingTone = soundFileMapper.find(s => s.name === tone);
    return existingTone ? buildOption(existingTone) : defaultSoundByType[type];
  }

  return defaultSoundByType[type] || defaultSoundByType['default'];
};
